import { ServiceManager } from "@kinetikos/rof";
import Cookies from "js-cookie";

const baseURL = process.env.REACT_APP_BACKEND_URL;

let _services = null;

const rofBasePath = process.env.REACT_APP_ROF_SDK_BASEPATH;

// auth helpers
const buildTokenHeaders = (contentLanguage = "en") => {
  const csrf = Cookies.get("csrftokenz") || Cookies.get("csrftoken") || "";
  return {
    credentials: "include",
    headers: {
      "X-CSRFToken": csrf,
      "X-Requested-With": "XMLHttpRequest",
      "Content-Type": "application/json",
      "Content-Language": contentLanguage,
      "X-Frame-Options": "sameorigin",
    },
  };
};

// SDK bootstrap
export const getRofServices = async (opts = {}) => {
  if (_services) return _services;

  const { contentLanguage = "en" } = opts;

  _services = ServiceManager.initialize({
    basePath: rofBasePath,
    tokenProxyUrl: `${baseURL}/cdss/rof/token/`,
    tokenProxyGetRequestOptions: async () => buildTokenHeaders(contentLanguage),
    tokenProxyRequestOptions: async () => buildTokenHeaders(contentLanguage),
  });

  return _services;
};

// Ensure patient payload has required fields before first creation in SDK.
const ensureHasPatientFields = (payload) => {
  const { age, gender_at_birth, height_cm } = payload || {};
  if (
    age == null ||
    gender_at_birth == null ||
    gender_at_birth === "" ||
    height_cm == null
  ) {
    const err = new Error(
      "Missing required patient fields for SDK (age, gender_at_birth, height_cm)",
    );
    err.code = "MISSING_ROF_PATIENT_FIELDS";
    throw err;
  }
};

// Create/ensure SDK patient & assessment
export const ensureSdkPatientAndAssessment = async ({
  sdkPatientId = null,
  sdkAssessmentId = null,
  patientPayload = null,
} = {}) => {
  const services = await getRofServices();

  const patientSvc =
    services?.patientService ||
    services?.PatientService ||
    services?.services?.PatientService;

  const assessmentSvc =
    services?.assessmentService ||
    services?.AssessmentService ||
    services?.services?.AssessmentService;

  if (!patientSvc || !assessmentSvc) {
    throw new Error(
      "RiskFall SDK services not available (patient/assessment).",
    );
  }

  let patientId = sdkPatientId;
  if (!patientId) {
    ensureHasPatientFields(patientPayload);
    const p = await patientSvc.createPatient({
      age: patientPayload.age,
      gender_at_birth: patientPayload.gender_at_birth,
      height_cm: patientPayload.height_cm,
    });
    patientId = p.id;
  }

  let assessmentId = sdkAssessmentId;
  if (!assessmentId) {
    const a = await assessmentSvc.createAssessment(patientId);
    assessmentId = a.id;
  }

  return { sdkPatientId: patientId, sdkAssessmentId: assessmentId };
};

// Link evaluation <-> sdk ids
export const linkEvaluationToRof = async (
  evaluationId,
  { sdkPatientId, sdkAssessmentId },
) => {
  if (!evaluationId) throw new Error("Missing evaluationId");
  if (!sdkPatientId || !sdkAssessmentId)
    throw new Error("Missing sdk ids to link");

  const res = await fetch(
    `${baseURL}/cdss/evaluation/${encodeURIComponent(evaluationId)}/rof/link/`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRFToken":
          Cookies.get("csrftokenz") || Cookies.get("csrftoken") || "",
      },
      body: JSON.stringify({
        sdkPatientId,
        sdkAssessmentId,
        sdk_patient_id: sdkPatientId,
        sdk_assessment_id: sdkAssessmentId,
      }),
    },
  );

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to link ROF ids (${res.status}): ${text}`);
  }
  return res.json();
};

const _rofLinkCache = new Map();
const _rofLinkInflight = new Map();

export const clearRofLinkCache = () => {
  _rofLinkCache.clear();
  _rofLinkInflight.clear();
};

export const getRofLinkForEvaluation = async (
  evaluationId,
  { forceRefresh = false } = {},
) => {
  if (!evaluationId) throw new Error("Missing evaluationId");
  const key = String(evaluationId);

  if (!forceRefresh && _rofLinkCache.has(key)) {
    return _rofLinkCache.get(key);
  }
  if (_rofLinkInflight.has(key)) {
    return _rofLinkInflight.get(key);
  }

  const p = (async () => {
    const res = await fetch(
      `${baseURL}/cdss/evaluation/${encodeURIComponent(evaluationId)}/rof/link/`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRFToken":
            Cookies.get("csrftokenz") || Cookies.get("csrftoken") || "",
        },
      },
    );

    if (res.status === 404) {
      _rofLinkCache.set(key, null);
      return null;
    }
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to get ROF link (${res.status}): ${text}`);
    }

    const payload = await res.json();
    const data = payload?.data ?? null;
    _rofLinkCache.set(key, data);
    return data;
  })().finally(() => _rofLinkInflight.delete(key));

  _rofLinkInflight.set(key, p);
  return p;
};

export const getEvaluationRofLink = async (evaluationId) => {
  const link = await getRofLinkForEvaluation(evaluationId);
  if (!link) return null;
  return {
    sdk_patient_id: link.sdk_patient_id ?? link.sdkPatientId ?? null,
    sdk_assessment_id: link.sdk_assessment_id ?? link.sdkAssessmentId ?? null,
  };
};
//  misc helpers for fetchers
const is404 = (err) =>
  err?.response?.status === 404 ||
  err?.status === 404 ||
  (typeof err?.message === "string" && /404/.test(err.message));

const try404 = async (fn) => {
  try {
    const v = await fn();
    return v ?? null;
  } catch (e) {
    if (is404(e)) return null;
    throw e;
  }
};

const firstAvailable = async (...fns) => {
  for (const f of fns) {
    const v = await try404(f);
    if (v != null) return v;
  }
  return null;
};

const _getAssessmentSvc = (services) =>
  services?.assessmentService ||
  services?.AssessmentService ||
  services?.services?.AssessmentService;

const _getSTS = async (services, assessmentId) =>
  firstAvailable(
    () => _getAssessmentSvc(services)?.getSTS?.(assessmentId),
  );

const _getTUG = async (services, assessmentId) =>
  firstAvailable(
    () => _getAssessmentSvc(services)?.getTUG?.(assessmentId),
  );

const _getShortFES = async (services, assessmentId) =>
  firstAvailable(
    () => _getAssessmentSvc(services)?.getShortFES?.(assessmentId),
  );

const _getFrailty = async (services, assessmentId) =>
  firstAvailable(
    () => _getAssessmentSvc(services)?.getFrailty?.(assessmentId),
  );

const _getMedicalHistory = async (services, assessmentId) =>
  firstAvailable(
    () => _getAssessmentSvc(services)?.getMedicalHistory?.(assessmentId),
  );

const _getAssessment = async (services, assessmentId) =>
  firstAvailable(
    () => _getAssessmentSvc(services)?.getAssessment?.(assessmentId),
  );

export const getRofAssessmentSummary = async (services, sdkAssessmentId) => {
  if (!services) services = await getRofServices();
  if (!sdkAssessmentId) throw new Error("Missing sdkAssessmentId");

  const [tug, sts, shortFES, assessment] = await Promise.all([
    _getTUG(services, sdkAssessmentId),
    _getSTS(services, sdkAssessmentId),
    _getShortFES(services, sdkAssessmentId),
    _getAssessment(services, sdkAssessmentId),
  ]);

  const num = (v) => (typeof v === "number" ? v : null);
  const tugSec =
    num(tug?.duration_s) ?? num(tug?.time_s) ?? num(tug?.time) ?? null;
  const stsSec =
    num(sts?.duration_s) ?? num(sts?.time_s) ?? num(sts?.time) ?? null;

  // Global score now comes from the assessment object itself
  let rofPct =
    num(assessment?.global_probability) != null
      ? Math.round(assessment.global_probability * 100)
      : null;

  if (rofPct == null) {
    const tugPart =
      typeof tugSec === "number"
        ? Math.max(0, Math.min(100, ((tugSec - 8) / 12) * 100))
        : null;
    const stsPart =
      typeof stsSec === "number"
        ? Math.max(0, Math.min(100, ((stsSec - 6) / 10) * 100))
        : null;

    const vals = [tugPart, stsPart].filter((v) => v != null);
    if (vals.length)
      rofPct = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }

  return {
    rofPct: rofPct != null ? Math.round(rofPct) : null,
    tugSec: tugSec ?? null,
    stsSec: stsSec ?? null,
    raw: { tug, sts, shortFES, assessment },
  };
};

// SDK deletion helper
export const deleteRofAssessment = async (sdkAssessmentId) => {
  if (!sdkAssessmentId) return;

  const services = await getRofServices();

  const assessmentSvc =
    services?.assessmentService ||
    services?.AssessmentService ||
    services?.services?.AssessmentService;

  if (!assessmentSvc?.deleteAssessment) {
    throw new Error("AssessmentService.deleteAssessment not available");
  }

  await assessmentSvc.deleteAssessment(sdkAssessmentId);
};

// score readers — scores now come from assessment + sub-endpoints
export const getAssessmentScores = async (assessmentId) => {
  if (!assessmentId) throw new Error("Missing assessmentId");
  const services = await getRofServices();
  const assessmentSvc = _getAssessmentSvc(services);

  const tryCall = async (fn) => {
    try {
      return await fn?.();
    } catch {
      return null;
    }
  };

  // Global score now lives on the assessment object itself
  const assessment = await tryCall(() =>
    assessmentSvc?.getAssessment?.(assessmentId),
  );
  const globalProbability =
    typeof assessment?.global_probability === "number"
      ? Math.round(assessment.global_probability * 100)
      : null;
  const globalLabel = assessment?.global_score ?? null;

  // Individual test data from assessment sub-endpoints
  const [sts, tug, sls, shortFes, frailty, medicalHistory] = await Promise.all([
    tryCall(() => assessmentSvc?.getSTS?.(assessmentId)),
    tryCall(() => assessmentSvc?.getTUG?.(assessmentId)),
    tryCall(() => assessmentSvc?.getSLS?.(assessmentId)),
    tryCall(() => assessmentSvc?.getShortFES?.(assessmentId)),
    tryCall(() => assessmentSvc?.getFrailty?.(assessmentId)),
    tryCall(() => assessmentSvc?.getMedicalHistory?.(assessmentId)),
  ]);

  const pickNum = (o, keys) => {
    for (const k of keys) {
      if (typeof o?.[k] === "number") return o[k];
    }
    return null;
  };

  return {
    globalProbability,
    globalLabel,
    sts: {
      raw: sts,
      value: pickNum(sts, [
        "sts_score",
        "score",
        "value",
        "seconds",
        "duration_s",
        "time_s",
        "time",
      ]),
    },
    tug: {
      raw: tug,
      value: pickNum(tug, [
        "score",
        "value",
        "seconds",
        "duration_s",
        "time_s",
        "time",
      ]),
    },
    sls: {
      raw: sls,
      value: pickNum(sls, [
        "score",
        "value",
        "seconds",
        "duration_s",
        "time_s",
        "time",
      ]),
    },
    shortFes: {
      raw: shortFes,
      value: pickNum(shortFes, ["score", "value", "total", "sum"]),
    },
    frailty: { raw: frailty, value: pickNum(frailty, ["score", "value"]) },
    medicalHistory: {
      raw: medicalHistory,
      value: pickNum(medicalHistory, ["score", "value"]),
    },
  };
};

const _firstAvailableCall = async (...fns) => {
  for (const f of fns) {
    try {
      const v = await f();
      if (v !== undefined) return v;
    } catch {}
  }
  throw new Error("No suitable SDK method available to persist score.");
};

// Global score now lives on the assessment — update via assessmentService
export const upsertGlobalScore = async (
  assessmentId,
  { probabilityPct, label },
) => {
  if (!assessmentId) throw new Error("Missing assessmentId");
  const services = await getRofServices();
  const assessmentSvc = _getAssessmentSvc(services);

  if (!assessmentSvc) throw new Error("assessmentService not available");

  const prob0to1 =
    typeof probabilityPct === "number" && probabilityPct >= 0
      ? Math.max(0, Math.min(100, probabilityPct)) / 100
      : undefined;

  const payload = {
    global_probability: prob0to1,
    global_score: label,
  };

  return _firstAvailableCall(
    () => assessmentSvc?.updateAssessment?.(assessmentId, payload),
    () => assessmentSvc?.patchAssessment?.(assessmentId, payload),
    () => assessmentSvc?.update?.(assessmentId, payload),
  );
};

// Component scores now live on assessment sub-endpoints
const _componentMethodMap = {
  sts: ["updateSTS", "patchSTS", "setSTS"],
  tug: ["updateTUG", "patchTUG", "setTUG"],
  sls: ["updateSLS", "patchSLS", "setSLS"],
  short_fes: ["updateShortFES", "patchShortFES", "setShortFES"],
  shortfes: ["updateShortFES", "patchShortFES", "setShortFES"],
  frailty: ["updateFrailty", "patchFrailty", "setFrailty"],
  medical_history: ["updateMedicalHistory", "patchMedicalHistory", "setMedicalHistory"],
  medicalhistory: ["updateMedicalHistory", "patchMedicalHistory", "setMedicalHistory"],
};

export const upsertComponentScore = async (assessmentId, key, payload = {}) => {
  if (!assessmentId) throw new Error("Missing assessmentId");
  if (!key) throw new Error("Missing component key");
  const services = await getRofServices();
  const assessmentSvc = _getAssessmentSvc(services);

  if (!assessmentSvc) throw new Error("assessmentService not available");

  // Normalize payload for interactive tests (sts/tug/sls)
  const normalized = (() => {
    const p = { ...payload };
    const k = key.toLowerCase();
    if (k === "sts" || k === "tug" || k === "sls") {
      const duration_s =
        typeof p.duration_s === "number"
          ? p.duration_s
          : typeof p.seconds === "number"
            ? p.seconds
            : typeof p.time_s === "number"
              ? p.time_s
              : undefined;
      return duration_s !== undefined ? { ...p, duration_s } : p;
    }
    return p;
  })();

  const methods = _componentMethodMap[key.toLowerCase()] || [];
  const calls = methods.map(
    (method) => () => assessmentSvc[method]?.(assessmentId, normalized),
  );
  return _firstAvailableCall(...calls);
};

// ===========================================
// SHARED DEMOGRAPHIC HELPERS
// ===========================================

export const calcAge = (dobIso) => {
  if (!dobIso) return null;
  const today = new Date();
  const dob = new Date(dobIso);
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
};

export const mapGenderAtBirthForSdk = (g) => {
  const x = String(g || "")
    .toLowerCase()
    .trim();
  if (x.startsWith("f") || x === "2") return "female";
  if (x.startsWith("m") || x === "1") return "male";
  if (x.startsWith("o") || x === "3") return "other";
  return null;
};

export const parseHeightCm = (pHome) => {
  const raw =
    pHome?.height_cm ?? pHome?.height ?? pHome?.stature_cm ?? pHome?.stature;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// ===========================================
// DEMOGRAPHIC SYNC
// ===========================================

/**
 * Ensures SDK patient has up-to-date demographics (gender, height, age)
 * matching the platform's current patient data.
 */
export const syncSdkPatientDemographics = async ({
  sdkPatientId,
  platformPatient,
}) => {
  if (!sdkPatientId || !platformPatient) return;

  try {
    const services = await getRofServices();
    const patientSvc =
      services?.patientService ||
      services?.PatientService ||
      services?.services?.PatientService;

    if (!patientSvc) {
      console.warn(
        "[ROF-Sync] PatientService not available to sync demographics.",
      );
      return;
    }

    // 1. Fetch SDK patient
    let sdkPatient;
    try {
      sdkPatient = await patientSvc.getPatient(sdkPatientId);
    } catch (e) {
      console.warn(
        `[ROF-Sync] Could not fetch SDK patient ${sdkPatientId}:`,
        e,
      );
      return;
    }

    if (!sdkPatient) return;

    // 2. Compute platform values
    const currentAge =
      calcAge(platformPatient.birthdate) ?? calcAge(platformPatient.birth_date);
    const currentGender =
      mapGenderAtBirthForSdk(platformPatient.gender) ||
      mapGenderAtBirthForSdk(platformPatient.gender_id);
    const currentHeight = parseHeightCm(platformPatient);

    // 3. Compare values (only update if mismatch found)
    const updates = {};
    let hasChanges = false;

    // Comparison helpers
    const toStr = (v) => (v === null || v === undefined ? "" : String(v));
    const toNum = (v) => Number(v);

    // Check Age
    if (currentAge != null && toNum(sdkPatient.age) !== toNum(currentAge)) {
      updates.age = currentAge;
      hasChanges = true;
    }

    // Check Gender
    if (
      currentGender &&
      toStr(sdkPatient.gender_at_birth).toLowerCase() !==
        currentGender.toLowerCase()
    ) {
      updates.gender_at_birth = currentGender;
      hasChanges = true;
    }

    // Check Height
    if (
      currentHeight != null &&
      toNum(sdkPatient.height_cm) !== toNum(currentHeight)
    ) {
      updates.height_cm = currentHeight;
      hasChanges = true;
    }

    // 4. Update if needed
    if (hasChanges) {
      // Logic to find the update method purely defensively
      const updateFn =
        patientSvc.updatePatient ||
        patientSvc.update ||
        patientSvc.patchPatient;

      if (typeof updateFn === "function") {
        console.log("[ROF-Sync] Patching SDK patient demographics:", updates);
        // Bind context just in case
        await updateFn.call(patientSvc, sdkPatientId, updates);
      } else {
        console.warn(
          "[ROF-Sync] No updatePatient method found. Available methods:",
          Object.keys(Object.getPrototypeOf(patientSvc) || {}),
        );
      }
    }
  } catch (err) {
    // Safety net: never throw from a background sync
    console.warn("[ROF-Sync] Unexpected error during sync:", err);
  }
};
